import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { differenceInDays } from "date-fns";

const getClaimsCosts = async (uid) => {
  try {
    const claimsCollection = collection(db, `users/${uid}/claims`);
    const claimsSnapshot = await getDocs(claimsCollection);

    const getMovements = async (claimRef) => {
      const movementsCollectionRef = collection(
        db,
        `${claimRef.path}/movements`
      );
      const movementsQuery = query(
        movementsCollectionRef,
        where("deleted", "==", false)
      );
      const movementsSnapshot = await getDocs(movementsQuery);

      let expenses = 0;
      let deductible = 0;
      let incomes = 0;

      movementsSnapshot.forEach((doc) => {
        const data = doc.data();
        const { amount, type } = data;
        if (type === "Gastos") expenses += amount;
        if (type === "Ingreso") incomes += amount;
        if (type === "Deducible") deductible += amount;
      });

      return { expenses, deductible, incomes };
    };

    const claimsPromises = claimsSnapshot.docs.map(async (claim) => {
      const claimData = claim.data();
      if (!claimData.isMock && !claimData.deleted && claim.ref) {
        const type = claimData.type[0];
        const { expenses, deductible, incomes } = await getMovements(claim.ref);
        const expensesSet = type === "vialAssistance" ? 2000 : expenses;
        return {
          id: claim.id,
          totalAmount: expensesSet - deductible - incomes,
        };
      }
      return null;
    });

    const claims = await Promise.all(claimsPromises);
    const validClaims = claims.filter((claim) => claim !== null);

    const totalClaimsCosts = validClaims.reduce((total, claim) => {
      return total + claim.totalAmount;
    }, 0);

    return totalClaimsCosts;
  } catch (error) {
    console.log("[Error getClaimsCosts]:", error);
  }
};

const devices = ["smartphone", "laptop", "tablet"];
const isADeviceCheck = (d) => devices.includes(d) || d === "devices";

const getCommissionPeriod = async (id, type) => {
  try {
    const paymentQuery = query(
      collectionGroup(db, "subPayments"),
      where("status", "in", ["succeeded", "refunded"]),
      where(isADeviceCheck(type) ? "sid" : "vid", "==", id)
    );

    const paymentSnapshot = await getDocs(paymentQuery);

    const totalAmount = paymentSnapshot.docs.reduce((sum, doc) => {
      const data = doc.data();
      return sum + (data.amount || 0);
    }, 0);

    return totalAmount;
  } catch (error) {
    console.error(
      `Error fetching commission period for vehicle ID: ${id}:`,
      error
    );
    return 0;
  }
};

const getClaimsAmount = async (uid) => {
  try {
    const claimsCollection = collection(db, `users/${uid}/claims`);
    const claimsQuery = query(claimsCollection, where("isMock", "==", 0));
    const claimsSnapshot = await getDocs(claimsQuery);
    const claimsAmount = claimsSnapshot.docs.length;
    return claimsAmount;
  } catch (error) {
    console.log("[Error getClaimsAmount]:", error);
  }
};

async function getExtras(uid, vehicleId) {
  try {
    const ref = collection(db, `users/${uid}/vehicles/${vehicleId}/extras`);
    const q = query(ref, where("isActive", "==", true));

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return [];
    }

    return snapshot.docs.map((doc) => {
      return {
        id: doc.id,
        ...doc.data(),
      };
    });
  } catch (error) {
    return Promise.reject(error);
  }
}

const getProductData = async (uid, product) => {
  const { id, type } = product;
  const productCollection = doc(
    db,
    `users/${uid}/${type === "smartphone" ? "smartphones" : "vehicles"}/${id}`
  );
  const productSnapshot = await getDoc(productCollection);
  return productSnapshot?.data() || null;
};

const getUserUtility = async (uid, product) => {
  try {
    const claimsAmount = await getClaimsAmount(uid);
    const productData = await getProductData(uid, product);
    const {
      insuranceStartDay,
      insuranceStartMonth,
      insuranceStartYear,
      insuranceStartTimestamp,
    } = productData;

    const startTimestamp = insuranceStartTimestamp
      ? insuranceStartTimestamp
      : new Date(
          insuranceStartYear,
          insuranceStartMonth - 1,
          insuranceStartDay
        ).getTime();

    const daysInsured = startTimestamp
      ? differenceInDays(new Date(), startTimestamp)
      : 0;

    return {
      userUtility: claimsAmount / (daysInsured / 365),
      daysInsured: daysInsured / 365,
    };
  } catch (error) {
    console.log("[Error getUserUtility]:", error);
  }
};

const getClaimRate = async (uid, product) => {
  const { id, type } = product;
  const claimsCosts = await getClaimsCosts(uid);
  const premiumPayed = await getCommissionPeriod(id, type);
  return claimsCosts / premiumPayed;
};

export {
  getUserUtility,
  getClaimsCosts,
  getCommissionPeriod,
  getClaimRate,
  getProductData,
  getExtras,
};
